import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import MoneyInput from '../components/MoneyInput';
import ClienteSearch from '../components/ClienteSearch';
import VinculacionModal from '../components/VinculacionModal';

const formatMoney = (n) => '$' + (n || 0).toLocaleString('es-AR');
const hoyStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const emptyForm = () => ({ monto: '', fecha: hoyStr(), concepto: '', categoria: '', medio_pago: 'Efectivo', tipo: 'eventual', cliente_id: '', expediente_id: '', tramite_id: '', modoCalculo: 'por_cuota', montoCuota: '', montoTotal: '', cantCuotas: '' });

export default function GastoModal({ open, onClose, editing = null, onSaved }) {
  const [clientes, setClientes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [tramites, setTramites] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [vincularOpen, setVincularOpen] = useState(false);

  const editingId = editing?.id || null;
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchClientes = useCallback(async () => {
    const { data } = await supabase.from('clientes').select('id, nombre, apellido').eq('eliminado', false).order('nombre');
    setClientes(data || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [, exp, tra, gas] = await Promise.all([
        fetchClientes(),
        supabase.from('expedientes').select('id, numero, caratula').eq('eliminado', false).order('caratula'),
        supabase.from('tramites').select('id, nombre').eq('eliminado', false).order('nombre'),
        supabase.from('gastos').select('categoria'),
      ]);
      setExpedientes(exp.data || []);
      setTramites(tra.data || []);
      setCategorias([...new Set((gas.data || []).map(g => g.categoria).filter(Boolean))]);
    })();
    if (editing) {
      setForm({ monto: editing.monto || '', fecha: editing.fecha || '', concepto: editing.concepto || '', categoria: editing.categoria || '', medio_pago: editing.medio_pago || 'Efectivo', tipo: editing.tipo || 'eventual', cliente_id: editing.cliente_id || '', expediente_id: editing.expediente_id || '', tramite_id: editing.tramite_id || '', modoCalculo: 'por_cuota', montoCuota: '', montoTotal: '', cantCuotas: '' });
    } else {
      setForm(emptyForm());
    }
  }, [open, editing, fetchClientes]);

  // Etiqueta del expediente/trámite vinculado
  let vinculo = null;
  if (form.expediente_id) {
    const e = expedientes.find(x => x.id === form.expediente_id);
    vinculo = { icon: '📁', tipo: 'Expediente', label: e ? `${e.numero ? e.numero + ' — ' : ''}${e.caratula || ''}` : 'Expediente' };
  } else if (form.tramite_id) {
    const t = tramites.find(x => x.id === form.tramite_id);
    vinculo = { icon: '📋', tipo: 'Trámite', label: t ? t.nombre : 'Trámite' };
  }

  const cuotaPreview = (() => {
    if (form.tipo !== 'deuda') return null;
    const cant = parseInt(form.cantCuotas);
    if (!cant || cant < 1) return null;
    if (form.modoCalculo === 'por_cuota') {
      const c = parseFloat(form.montoCuota);
      if (!c || c <= 0) return null;
      return { cuota: c, total: c * cant, cant };
    } else {
      const t = parseFloat(form.montoTotal);
      if (!t || t <= 0) return null;
      return { cuota: t / cant, total: t, cant };
    }
  })();

  async function saveGasto() {
    setSaving(true);
    if (form.tipo === 'deuda') {
      const cant = parseInt(form.cantCuotas);
      if (!cant || cant < 1) { setSaving(false); showToast('Ingresá la cantidad de cuotas'); return; }
      let montoCuota;
      if (form.modoCalculo === 'por_cuota') {
        montoCuota = parseFloat(form.montoCuota);
        if (!montoCuota || montoCuota <= 0) { setSaving(false); showToast('Ingresá el valor de cada cuota'); return; }
      } else {
        const total = parseFloat(form.montoTotal);
        if (!total || total <= 0) { setSaving(false); showToast('Ingresá el monto total'); return; }
        montoCuota = total / cant;
      }
      const baseDate = form.fecha ? new Date(form.fecha + 'T12:00:00') : new Date();
      const registros = Array.from({ length: cant }, (_, i) => {
        const d = new Date(baseDate);
        d.setMonth(d.getMonth() + i);
        return {
          monto: parseFloat(montoCuota.toFixed(2)),
          fecha: d.toISOString().split('T')[0],
          concepto: `${form.concepto || 'Deuda'} (cuota ${i + 1}/${cant})`,
          categoria: form.categoria,
          medio_pago: form.medio_pago,
          tipo: 'deuda',
          cliente_id: form.cliente_id || null,
          expediente_id: form.expediente_id || null,
          tramite_id: form.tramite_id || null,
        };
      });
      const { error } = await supabase.from('gastos').insert(registros);
      setSaving(false);
      if (error) { showToast('Error al guardar'); return; }
      showToast(`${cant} cuotas registradas ✓`);
      onSaved?.();
      onClose();
      return;
    }
    if (!form.monto || parseFloat(form.monto) <= 0) { setSaving(false); showToast('Ingresá un monto válido'); return; }
    const gastoData = { monto: parseFloat(form.monto), fecha: form.fecha || null, concepto: form.concepto, categoria: form.categoria, medio_pago: form.medio_pago, tipo: form.tipo, cliente_id: form.cliente_id || null, expediente_id: form.expediente_id || null, tramite_id: form.tramite_id || null };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('gastos').update(gastoData).eq('id', editingId));
    } else {
      const result = await supabase.from('gastos').insert(gastoData).select('id').single();
      error = result.error;
    }
    setSaving(false);
    if (error) { showToast('Error al guardar'); return; }
    showToast(editingId ? 'Gasto actualizado ✓' : 'Gasto registrado ✓');
    onSaved?.();
    onClose();
  }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
        <div className="modal" style={{ width: 540 }}>
          <div className="modal-header">
            <div className="modal-title">{editingId ? 'Editar gasto' : 'Registrar gasto'}</div>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <div className="modal-body">
            <div className="form-grid">
              {form.tipo !== 'deuda' && (
                <div className="form-group">
                  <label className="form-label">Monto ($) *</label>
                  <MoneyInput value={form.monto} onChange={v => setForm(f => ({ ...f, monto: v }))} autoFocus />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Fecha {form.tipo === 'deuda' ? '(primera cuota)' : ''}</label>
                <input className="form-input" type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Concepto</label>
                <input className="form-input" value={form.concepto} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Alquiler, Sellado" />
              </div>
              <div className="form-group">
                <label className="form-label">Categoría (libre)</label>
                <input className="form-input" value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))} placeholder="Ej: Administrativo" list="cats-modal" />
                <datalist id="cats-modal">{categorias.map(c => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select className="form-select" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                  <option value="eventual">Eventual</option>
                  <option value="fijo">Fijo / Corriente</option>
                  <option value="deuda">Deuda en cuotas</option>
                </select>
              </div>
              {form.tipo === 'deuda' && (
                <div className="form-group form-full">
                  <label className="form-label">Modo de cálculo</label>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <button type="button" onClick={() => setForm(f => ({ ...f, modoCalculo: 'por_cuota' }))}
                      style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: '1.5px solid', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: form.modoCalculo === 'por_cuota' ? 'var(--blue)' : 'var(--surface2)', color: form.modoCalculo === 'por_cuota' ? '#fff' : 'var(--text)', borderColor: form.modoCalculo === 'por_cuota' ? 'var(--blue)' : 'var(--border)' }}>
                      Valor por cuota
                    </button>
                    <button type="button" onClick={() => setForm(f => ({ ...f, modoCalculo: 'por_total' }))}
                      style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: '1.5px solid', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: form.modoCalculo === 'por_total' ? 'var(--blue)' : 'var(--surface2)', color: form.modoCalculo === 'por_total' ? '#fff' : 'var(--text)', borderColor: form.modoCalculo === 'por_total' ? 'var(--blue)' : 'var(--border)' }}>
                      Monto total
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {form.modoCalculo === 'por_cuota' ? (
                      <div style={{ flex: 1 }}>
                        <label className="form-label">Valor de cada cuota ($)</label>
                        <MoneyInput value={form.montoCuota} onChange={v => setForm(f => ({ ...f, montoCuota: v }))} />
                      </div>
                    ) : (
                      <div style={{ flex: 1 }}>
                        <label className="form-label">Monto total ($)</label>
                        <MoneyInput value={form.montoTotal} onChange={v => setForm(f => ({ ...f, montoTotal: v }))} />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <label className="form-label">Cantidad de cuotas</label>
                      <input className="form-input" type="number" min="1" value={form.cantCuotas} onChange={e => setForm(f => ({ ...f, cantCuotas: e.target.value }))} onWheel={e => e.currentTarget.blur()} placeholder="Ej: 12" />
                    </div>
                  </div>
                  {cuotaPreview && (
                    <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, color: 'var(--text)', display: 'flex', gap: 20 }}>
                      <span>Cuota: <strong style={{ color: 'var(--blue)', fontFamily: 'DM Mono' }}>{formatMoney(cuotaPreview.cuota)}</strong></span>
                      <span>Total: <strong style={{ fontFamily: 'DM Mono' }}>{formatMoney(cuotaPreview.total)}</strong></span>
                      <span>Se crearán <strong>{cuotaPreview.cant}</strong> registros mensuales</span>
                    </div>
                  )}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Medio de pago</label>
                <select className="form-select" value={form.medio_pago} onChange={e => setForm(f => ({ ...f, medio_pago: e.target.value }))}>
                  <option>Efectivo</option><option>Transferencia</option><option>Cheque</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Cliente vinculado</label>
                <ClienteSearch clientes={clientes} value={form.cliente_id} onChange={id => setForm(f => ({ ...f, cliente_id: id }))} allowCreate onClienteCreado={fetchClientes} />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Expediente / Trámite vinculado</label>
                {vinculo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 8 }}>
                    <span style={{ fontSize: 16, flexShrink: 0 }}>{vinculo.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: 'var(--text4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{vinculo.tipo}</div>
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vinculo.label}</div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVincularOpen(true)} style={{ flexShrink: 0 }}>Cambiar</button>
                  </div>
                ) : (
                  <button className="btn btn-ghost" onClick={() => setVincularOpen(true)} style={{ width: '100%', justifyContent: 'center' }}>
                    + Vincular expediente o trámite
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveGasto} disabled={saving}>{saving ? 'Guardando…' : 'Guardar gasto'}</button>
          </div>
        </div>
      </div>

      <VinculacionModal
        open={vincularOpen}
        onClose={() => setVincularOpen(false)}
        tabs={['expedientes', 'tramites']}
        value={{ expediente_id: form.expediente_id, tramite_id: form.tramite_id }}
        onSelect={(v) => setForm(f => ({ ...f, expediente_id: v.expediente_id, tramite_id: v.tramite_id }))}
      />

      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
